import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { firestoreDb } from '../../services/firebase'
import type { useAuth } from '../../context/useAuth'
import type { useRecurringLens } from '../../context/recurringLensContextCore'
import type { ChecklistItem as BoardChecklistItem, ChecklistMemberOption, ChecklistWorkflowStatus } from '../../types/schema'
import type { TimelineElement } from '../../context/TimelineContext'
import type { useTimeline } from '../../context/useTimeline'
import type { useWorkspace } from '../../context/useWorkspace'
import { emitSensoryEvent } from '../../services/aliveness/SensoryEventBus'
import { projectOptimisticRecurringTick, revertOptimisticRecurringTick } from '../ChecklistBoard/optimisticTick'
import { emitTrackEvent, emitWorkAction } from '../../utils/gamificationEvents'
import { updateChecklistWorkflowStatus } from '../../services/checklistStatusWriter'
import { createDeltaOperationId, ChecklistBlockerError } from '../../services/stripboardDeltaService'
import { getCompletionBlockReasons, getOpenBlockerTitles } from '../../utils/checklistNodeMeta'
import { getDistanceMeters, readCurrentCoordinates } from './canvasMisc'
import {
  assignChecklistItem,
  updateChecklistFields,
  workflowStatusToChecklistBoardStatus,
} from '../../services/checklistMutationService'
import { nextStatusAfterAssignment } from '../../services/checklistStatusMachine'
import { moveChecklistToBin } from '../../services/checklistBinService'
import { executeChecklistMutation } from '../../services/cascadeEngine'
import { writeChecklistUpdate } from '../../services/checklistWriter'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'
import { buildLockGraph, resolveLock, loadLockPages } from '../../services/checklistLockService'
import { getSourceChecklistId, nodesToElementChecklist } from './liveReconciliation'
import {
  getChecklistOperationalAccess,
  type ChecklistBaseOperationalPermissions,
} from '../../utils/checklistOperationalPermissions'
import {
  normalizeWeightage,
  normalizeComplexityStars,
} from './boardItem'
import { truncateToLimit } from '../../utils/checklistLimits'

export interface UseNodeStatusParams {
  canPerformOnNode: ReturnType<typeof useTimeline>['canPerformOnNode']
  currentUser: ReturnType<typeof useAuth>['currentUser']
  activeRole: ReturnType<typeof useWorkspace>['activeRole']
  canInteractWithChecklist: boolean
  checklistLimits: { titleMaxLength: number; weightageMax: number }
  canEditStructure: boolean
  setNodes: Dispatch<SetStateAction<BoardChecklistItem[]>>
  currentDisplayName: string
  refreshMarkdownSyncMetadata: () => void
  hasElementMirror: boolean
  recurringLens: ReturnType<typeof useRecurringLens>
  showFlash: (message: string) => void
  applyStripboardDelta: (params: any) => Promise<any>
  item: TimelineElement
  spawnAutoTemplateNodes: (sourceNode: BoardChecklistItem) => Promise<void>
  isDebugCanvas: boolean
  canDeleteChecklist: boolean
  deletedSourceIdsRef: MutableRefObject<Set<string>>
  nodes: BoardChecklistItem[]
  shouldMirrorNodeToElement: (node: BoardChecklistItem) => boolean
  lastChecklistMirrorRef: MutableRefObject<string | null>
  setSelectedNodeId: Dispatch<SetStateAction<string | null>>
  setVisionboardChecklistItems: Dispatch<SetStateAction<BoardChecklistItem[]>>
  elementById: Map<string, TimelineElement>
}

export function useNodeStatus({
  canPerformOnNode,
  currentUser,
  activeRole,
  canInteractWithChecklist,
  checklistLimits,
  canEditStructure,
  setNodes,
  currentDisplayName,
  refreshMarkdownSyncMetadata,
  hasElementMirror,
  recurringLens,
  showFlash,
  applyStripboardDelta,
  item,
  spawnAutoTemplateNodes,
  isDebugCanvas,
  canDeleteChecklist,
  deletedSourceIdsRef,
  nodes,
  shouldMirrorNodeToElement,
  lastChecklistMirrorRef,
  setSelectedNodeId,
  setVisionboardChecklistItems,
  elementById,
}: UseNodeStatusParams) {
  const { todayIso, weekKey, timeZone } = useWorkspaceDay(item.workspaceId)

  const getNodeOperationalAccess = (node: Pick<BoardChecklistItem, 'id' | 'assigneeId' | 'isHelperVisible'>) => {
    const nodeBasePermissions: ChecklistBaseOperationalPermissions = {
      assign: canPerformOnNode('checklist.assign', node.id),
      status: canPerformOnNode('checklist.change_status', node.id),
      proof: canPerformOnNode('checklist.manage_proof', node.id),
      memo: canPerformOnNode('checklist.manage_memo', node.id),
      comment: canPerformOnNode('checklist.comment', node.id),
      locationCheckin: canPerformOnNode('checklist.location_checkin', node.id),
    }
    return getChecklistOperationalAccess({
      item: node,
      currentUserId: currentUser?.uid ?? null,
      activeRole,
      isReadOnly: !canInteractWithChecklist,
      basePermissions: nodeBasePermissions,
    })
  }
  const updateTitle = async (node: BoardChecklistItem, title: string) => {
    const nextTitle = truncateToLimit(title.trim(), checklistLimits.titleMaxLength)
    if (nextTitle === node.title || !canEditStructure) return
    setNodes((currentNodes) =>
      currentNodes.map((candidate) => candidate.id === node.id ? { ...candidate, title: nextTitle } : candidate),
    )
    await updateChecklistFields(firestoreDb, {
      item: { ...node, title: nextTitle },
      updates: { title: nextTitle },
      actor: { id: currentUser?.uid, name: currentDisplayName || currentUser?.email },
      summary: `Renamed checklist item from ${node.title} to ${nextTitle}`,
      metadata: { changedFields: ['title'] },
    })
    refreshMarkdownSyncMetadata()
  }

  const updateWeightage = async (node: BoardChecklistItem, weightage: string) => {
    if (!canEditStructure) return
    const nextWeightage = Math.min(checklistLimits.weightageMax, normalizeWeightage(Number(weightage)))
    if (nextWeightage === normalizeWeightage(node.weightage)) return
    setNodes((currentNodes) =>
      currentNodes.map((candidate) => candidate.id === node.id ? { ...candidate, weightage: nextWeightage } : candidate),
    )
    await updateChecklistFields(firestoreDb, {
      item: node,
      updates: { weightage: nextWeightage },
      actor: { id: currentUser?.uid, name: currentDisplayName || currentUser?.email },
      summary: `Changed weight from ${normalizeWeightage(node.weightage)} to ${nextWeightage}`,
      metadata: { changedFields: ['weightage'] },
    })
    refreshMarkdownSyncMetadata()
    if (!hasElementMirror) return
    if (node.isCompleted) {
      await executeChecklistMutation(firestoreDb, node.id, true)
    } else {
      await executeChecklistMutation(firestoreDb, node.id, false)
    }
  }

  // handleWeightDialWheel removed — the bubble no longer has a weight knob.
  // Weight is edited via the drawer's ScrollDial (deferred to drawer Save).

  const emitChecklistCompletion = (node: BoardChecklistItem) => {
    emitTrackEvent('TRACK_FINISH_CHECK')
    emitWorkAction({
      kind: 'CHECKLIST_COMPLETE',
      workspaceId: item.workspaceId,
      subNodeId: node.id,
    })
  }

  const executeNodeCompletion = async (node: BoardChecklistItem, isCompleted: boolean) => {
    // Under a RecurringLens (Daily / Weekly canvas), completion is per
    // occurrence — route through setOccurrenceCompletion which writes the
    // `occurrences[focusedKey]` entry and mirrors to top-level fields when
    // the key is the current occurrence. Skips cascadeEngine (recurring
    // items don't roll progress up to a parent element).
    if (recurringLens && (node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY')) {
      const focusedKey = recurringLens.focusedKey
      const currentEntry = node.occurrences?.[focusedKey]
      const wasCompleted = Boolean(currentEntry?.isCompleted)
      const isNewCompletion = isCompleted && !wasCompleted
      const controller = new AbortController()
      emitSensoryEvent('MUTATION_TRIGGERED', { source: 'checklist-completion', targetId: node.id }, { signal: controller.signal })
      // Optimistic tick — the projected `occurrences` map on a recurring
      // node only carries the focused page's entry (see
      // checklistViewAdapter.projectDefinitionToChecklistItem). Without
      // this local update, ticking a card on any non-today Daily / Weekly
      // page persists to Firestore but never flips the .isDone class in
      // the DOM because TimelineContext's book-model subscription only
      // watches TODAY's page — the tapping user sees "nothing happened"
      // and double-toggles. Rollback on the catch below if the write
      // rejects.
      const previousOccurrences = node.occurrences
      setNodes((currentNodes) =>
        currentNodes.map((candidate) => candidate.id === node.id
          ? projectOptimisticRecurringTick(candidate, focusedKey, isCompleted)
          : candidate,
        ),
      )
      try {
        const nextWorkflow = isCompleted ? 'COMPLETED' : 'ONGOING'
        const currentLane = (currentEntry?.boardStatus as any) ?? (wasCompleted ? 'COMPLETE' : 'TODO')
        await updateChecklistWorkflowStatus({
          item: node,
          workflowStatus: nextWorkflow,
          periodKey: focusedKey,
          periodBoardStatus: currentLane,
          runDelta: applyStripboardDelta,
          actor: currentUser?.uid ? { id: currentUser.uid, name: currentDisplayName || currentUser?.email || undefined } : undefined,
        })
        if (isNewCompletion) emitChecklistCompletion(node)
        emitSensoryEvent('MUTATION_SUCCESS', { source: 'checklist-completion', targetId: node.id }, { signal: controller.signal })
      } catch (error) {
        // Persistence failed — roll back the optimistic update so the DOM
        // no longer lies about the tick state.
        setNodes((currentNodes) =>
          currentNodes.map((candidate) => candidate.id === node.id
            ? revertOptimisticRecurringTick(candidate, previousOccurrences)
            : candidate,
          ),
        )
        controller.abort()
        emitSensoryEvent('MUTATION_ERROR', { source: 'checklist-completion', targetId: node.id })
        if (error instanceof ChecklistBlockerError) {
          const first = error.blockers[0]
          if (first) {
            showFlash(first.message)
          }
        }
        throw error
      }
      return
    }

    const isNewCompletion = isCompleted && !node.isCompleted
    const controller = new AbortController()
    emitSensoryEvent('MUTATION_TRIGGERED', { source: 'checklist-completion', targetId: node.id }, { signal: controller.signal })
    try {
      const isRepeating = node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY'
      const periodKey = isRepeating ? (recurringLens?.focusedKey ?? (node.checklistType === 'WEEKLY' ? weekKey : todayIso)) : null
      if (!hasElementMirror) {
        // Aggregate MAIN canvas and recurring lens both mount with a parent
        // that is not a real element (workspace-default or synthetic). Skip
        // the cascade-engine call (which would try to load a nonexistent
        // parent element) and toggle isCompleted directly.
        await writeChecklistUpdate(
          node.id,
          { isCompleted, workflowStatus: isCompleted ? 'COMPLETED' : 'ONGOING', boardStatus: isCompleted ? 'COMPLETE' : 'IN_PROGRESS' },
          { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null },
          { pageKey: periodKey },
        )
        setNodes((currentNodes) =>
          currentNodes.map((candidate) => candidate.id === node.id ? { ...candidate, isCompleted } : candidate),
        )
        if (isNewCompletion) emitChecklistCompletion(node)
        emitSensoryEvent('MUTATION_SUCCESS', { source: 'checklist-completion', targetId: node.id }, { signal: controller.signal })
        return
      }
      await executeChecklistMutation(firestoreDb, node.id, isCompleted)
      if (isNewCompletion) {
        await spawnAutoTemplateNodes(node)
        emitChecklistCompletion(node)
      }
      emitSensoryEvent('MUTATION_SUCCESS', { source: 'checklist-completion', targetId: node.id }, { signal: controller.signal })
    } catch (error) {
      controller.abort()
      emitSensoryEvent('MUTATION_ERROR', { source: 'checklist-completion', targetId: node.id })
      throw error
    }
  }

  // toggleNode removed — the canvas bubble no longer has a checkbox. Node
  // completion is now driven by the drawer's status dropdown via
  // updateNodeStatus, which centralises proof / location / blocker gating.

  const updateNodeVerification = async (
    node: BoardChecklistItem,
    updates: Partial<BoardChecklistItem>,
    notRequiredScope?: 'FROM_PERIOD' | 'CLEAR' | null,
  ) => {
    if (!canInteractWithChecklist) return
    const changedKeys = Object.keys(updates)
    const access = getNodeOperationalAccess(node).permissions
    const isCompletionUpdate = updates.workflowStatus === 'COMPLETED' || updates.isCompleted === true
    const permitted = changedKeys.some((key) => ['workflowStatus', 'isCompleted'].includes(key))
      ? isCompletionUpdate ? access.complete : access.status
      : changedKeys.includes('verifiedCoordinates')
        ? access.locationCheckin
      : changedKeys.some((key) => ['proofFileUrl', 'proofFileName', 'proofFileStoragePath', 'proofUploadedById', 'proofUploadedByName', 'proofUploadedAt'].includes(key))
      ? access.proof
      : changedKeys.some((key) => ['proofVideoUrl', 'voiceMemoBlobUrl', 'memoStoragePath', 'memoFileName', 'memoContentType', 'memoUploadedById', 'memoUploadedByName', 'memoUploadedAt'].includes(key))
        ? access.memo
        : canEditStructure
    if (!permitted) return
    if (isDebugCanvas) {
      setNodes((currentNodes) => currentNodes.map((candidate) => candidate.id === node.id ? {
        ...candidate,
        ...updates,
        ...(updates.workflowStatus ? {
          boardStatus: updates.workflowStatus === 'COMPLETED' || updates.workflowStatus === 'NOT_REQUIRED'
            ? 'COMPLETE'
            : updates.workflowStatus === 'ONGOING' || updates.workflowStatus === 'POSTPONED'
              ? 'IN_PROGRESS'
              : 'TODO',
          isCompleted: updates.workflowStatus === 'COMPLETED',
        } : {}),
      } : candidate))
      refreshMarkdownSyncMetadata()
      return
    }
    const directUpdates: Partial<BoardChecklistItem> = { ...updates }
    let expectedVersion = node.recordVersion
    let boardStatus = node.boardStatus
    if ('scheduleStartDate' in updates || 'scheduleEndDate' in updates || 'startDate' in updates || 'endDate' in updates) {
      const result = await applyStripboardDelta({
        operationId: createDeltaOperationId('checklist-dates', node.id),
        operationType: 'DATE_UPDATE',
        recordId: node.id,
        recordKind: 'CHECKLIST',
        sourceStatus: boardStatus,
        destinationStatus: boardStatus,
        expectedVersion,
        startDate: updates.startDate ?? updates.scheduleStartDate ?? node.startDate ?? node.scheduleStartDate ?? null,
        endDate: updates.endDate ?? updates.scheduleEndDate ?? node.endDate ?? node.scheduleEndDate ?? null,
      })
      expectedVersion = result.recordVersion
      boardStatus = result.boardStatus
      delete directUpdates.startDate
      delete directUpdates.endDate
      delete directUpdates.scheduleStartDate
      delete directUpdates.scheduleEndDate
    }
    if ('priority' in updates || 'urgencyStars' in updates || 'importanceStars' in updates) {
      const priority = updates.priority ?? Math.ceil(((updates.urgencyStars ?? node.urgencyStars ?? 1) + (updates.importanceStars ?? node.importanceStars ?? 1)) / 2)
      const result = await applyStripboardDelta({
        operationId: createDeltaOperationId('checklist-priority', node.id),
        operationType: 'PRIORITY_UPDATE',
        recordId: node.id,
        recordKind: 'CHECKLIST',
        sourceStatus: boardStatus,
        destinationStatus: boardStatus,
        expectedVersion,
        priority,
      })
      expectedVersion = result.recordVersion
      delete directUpdates.priority
    }
    if ('workflowStatus' in updates) {
      const result = await updateChecklistWorkflowStatus({
        item: node,
        workflowStatus: updates.workflowStatus,
        coordinates: updates.verifiedCoordinates ?? null,
        runDelta: applyStripboardDelta,
        actor: { id: currentUser?.uid, name: currentDisplayName || currentUser?.email },
        periodKey: recurringLens ? recurringLens.focusedKey : null,
        periodBoardStatus: recurringLens ? node.occurrences?.[recurringLens.focusedKey]?.boardStatus : undefined,
        notRequiredScope,
      })
      setNodes((currentNodes) => currentNodes.map((candidate) => candidate.id === node.id ? {
        ...candidate,
        boardStatus: result.boardStatus,
        workflowStatus: result.workflowStatus ?? updates.workflowStatus,
        isCompleted: (result.workflowStatus ?? updates.workflowStatus) === 'COMPLETED',
        recordVersion: result.recordVersion,
        ...(updates.verifiedCoordinates ? { verifiedCoordinates: updates.verifiedCoordinates } : {}),
      } : candidate))
      refreshMarkdownSyncMetadata()
      return
    }
    setNodes((currentNodes) =>
      currentNodes.map((candidate) => candidate.id === node.id ? { ...candidate, ...updates, boardStatus, recordVersion: expectedVersion } : candidate),
    )
    if (Object.keys(directUpdates).length === 0) return
    await updateChecklistFields(firestoreDb, {
      item: node,
      updates: directUpdates as Record<string, unknown>,
      actor: { id: currentUser?.uid, name: currentDisplayName || currentUser?.email },
      action: updates.proofFileUrl !== undefined || updates.verifiedCoordinates !== undefined
        ? 'CHECKLIST_PROOF_UPDATED'
        : 'CHECKLIST_UPDATED',
      summary: updates.proofFileUrl !== undefined || updates.verifiedCoordinates !== undefined
        ? 'Updated completion verification'
        : `Updated ${Object.keys(updates).join(', ')}`,
      metadata: { changedFields: Object.keys(updates) },
    })
    refreshMarkdownSyncMetadata()
    if (typeof updates.isCompleted === 'boolean') {
      await executeNodeCompletion(node, updates.isCompleted)
    }
  }

  /** Persist an assignment change for a canvas node. On recurring items
   *  (DAILY / WEEKLY) with a focused occurrence, callers can pick a scope
   *  ('single' / 'future' / 'global'); scope routing is handled by
   *  `assignChecklistItem` in checklistMutationService. The canvas
   *  currently only surfaces a picker without a scope UI, so it always
   *  passes `scope = 'global'` when no scope is supplied — matching the
   *  pre-scope behavior on that surface. Higher-level surfaces (
   *  `NodeDetailDrawer`, `MemberChecklistDetailPage`) prompt the user
   *  and pass an explicit scope. */
  const updateNodeAssignment = async (
    node: BoardChecklistItem,
    assignee: ChecklistMemberOption | null,
    opts: { scope?: 'single' | 'future' | 'global' | null; pageKey?: string | null } = {},
  ) => {
    if (!canInteractWithChecklist || !currentUser || !getNodeOperationalAccess(node).permissions.assign) return
    const nextStatus = nextStatusAfterAssignment(node.workflowStatus, Boolean(assignee?.userId))
    const statusPatch = nextStatus ? {
      workflowStatus: nextStatus,
      boardStatus: workflowStatusToChecklistBoardStatus(nextStatus),
      isCompleted: false,
    } : {}

    if (isDebugCanvas) {
      setNodes((currentNodes) => currentNodes.map((candidate) => candidate.id === node.id
        ? {
            ...candidate,
            assigneeId: assignee?.userId ?? null,
            assigneeName: assignee?.displayName ?? null,
            assigneeHistoryIds: assignee?.userId
              ? [...new Set([...(candidate.assigneeHistoryIds ?? []), assignee.userId])]
              : candidate.assigneeHistoryIds,
            ...statusPatch,
          }
        : candidate))
      return
    }
    const isRecurring = node.checklistType === 'DAILY' || node.checklistType === 'WEEKLY'
    const scope = opts.scope ?? 'global'
    const pageKey = opts.pageKey
      ?? (isRecurring && scope !== 'global' ? recurringLens?.focusedKey ?? null : null)
    try {
      await assignChecklistItem(firestoreDb, {
        item: node,
        assignee,
        actor: {
          id: currentUser.uid,
          name: currentDisplayName || currentUser.email || 'Teammate',
        },
        pageKey,
        scope,
      })
    } catch (error) {
      const code = (error as { code?: string })?.code ?? 'unknown'
      console.error('[ChecklistDrawingBoard] assignment failed', {
        code,
        nodeId: node.id,
        checklistType: node.checklistType ?? 'INDIVIDUAL',
        scope,
        pageKey,
        error,
      })
      showFlash(code === 'permission-denied'
        ? 'You don\'t have permission to change this assignee.'
        : 'Could not update the assignee. Try again.')
      return
    }
    // Only reflect the change on the local canvas state for global scope
    // (definition-level). Scoped ('single' / 'future') assignments live on
    // page.detailPatch / detailVersion — the page subscription re-projects
    // them; touching the top-level assigneeId here would fight that.
    if (scope === 'global') {
      setNodes((currentNodes) => currentNodes.map((candidate) => candidate.id === node.id
        ? {
            ...candidate,
            assigneeId: assignee?.userId ?? null,
            assigneeName: assignee?.displayName ?? null,
            assigneeHistoryIds: assignee?.userId
              ? [...new Set([...(candidate.assigneeHistoryIds ?? []), assignee.userId])]
              : candidate.assigneeHistoryIds,
            ...statusPatch,
          }
        : candidate))
    }
  }

  const getConnectorRendering = (source: BoardChecklistItem) => {
    const complexityStars = normalizeComplexityStars(source.complexityStars)
    if (complexityStars === 5) {
      return {
        strokeWidth: 6,
        opacity: 1,
        className: 'checklistBoardWire animate-pulse',
        stroke: 'var(--danger)',
      }
    }
    if (complexityStars >= 3) {
      return {
        strokeWidth: 4,
        opacity: 0.8,
        className: 'checklistBoardWire',
        stroke: undefined,
      }
    }
    return {
      strokeWidth: 2,
      opacity: 0.5,
      className: 'checklistBoardWire',
      stroke: undefined,
    }
  }

  // Header workflow-status changes. Only COMPLETED is gated (proof file,
  // location check-in, and no open "is blocked by" dependency); every other
  // status is freely selectable. When COMPLETED is blocked we surface ALL
  // reasons in one flash. Picking a non-completed status un-completes a node
  // that was previously Done.
  const updateNodeStatus = async (
    node: BoardChecklistItem,
    status: ChecklistWorkflowStatus,
    opts?: { notRequiredScope?: 'FROM_PERIOD' | 'CLEAR' | null },
  ) => {
    const access = getNodeOperationalAccess(node)
    if (status === 'COMPLETED' ? !access.permissions.complete : !access.permissions.status) {
      showFlash(status === 'COMPLETED'
        ? access.completionDisabledReason ?? access.workDisabledReason ?? 'Your role cannot complete this checklist item.'
        : access.workDisabledReason ?? 'Your role cannot change this checklist status.')
      return
    }

    const effectiveNotRequiredScope =
      opts?.notRequiredScope !== undefined
        ? opts.notRequiredScope
        : status !== 'NOT_REQUIRED' && node.notRequiredFrom
          ? 'CLEAR'
          : null

    try {
      if (status !== 'COMPLETED') {
        await updateNodeVerification(
          node,
          {
            workflowStatus: status,
            ...(node.isCompleted ? { isCompleted: false } : {}),
          },
          effectiveNotRequiredScope,
        )
        return
      }

      const openBlockerTitles = getOpenBlockerTitles(
        node.blockedByNodeIds,
        (targetId) => elementById.get(targetId)?.progress,
      )
      const graph = buildLockGraph(nodes)
      const periodKey = recurringLens ? recurringLens.focusedKey : (node.checklistType === 'WEEKLY' ? weekKey : todayIso)
      const pagesMap = await loadLockPages(item.workspaceId, [{ itemId: node.id, periodKey }], graph, undefined, timeZone)
      const lockState = resolveLock(node, periodKey, graph, pagesMap, undefined, timeZone)
      const lockReasons = (lockState.isLocked ?? lockState.isBlocked)
        ? lockState.reasons.map((r) => r.title || r.targetId).filter(Boolean) as string[]
        : []
      const combinedBlockerTitles = Array.from(new Set([...openBlockerTitles, ...lockReasons]))
      const reasons = getCompletionBlockReasons(node, { openBlockerTitles: combinedBlockerTitles })
      if (reasons.length > 0) {
        showFlash(reasons.join(' • '))
        return
      }

      if (node.requiresLocationPin && node.targetCoordinates) {
        if (!access.permissions.locationCheckin) {
          showFlash(access.completionDisabledReason ?? access.workDisabledReason ?? 'Your role cannot submit a location check-in.')
          return
        }
        try {
          const verifiedCoordinates = await readCurrentCoordinates()
          if (getDistanceMeters(node.targetCoordinates, verifiedCoordinates) > 50) {
            showFlash('Verification Rejected: you must be physically on-site to mark this complete.')
            return
          }
          await updateNodeVerification(node, { workflowStatus: 'COMPLETED', isCompleted: true, verifiedCoordinates })
          refreshMarkdownSyncMetadata()
          return
        } catch (error) {
          showFlash(error instanceof Error ? error.message : 'Verification Rejected: location check failed.')
          return
        }
      }

      await updateNodeVerification(node, { workflowStatus: 'COMPLETED', isCompleted: true })
      refreshMarkdownSyncMetadata()
    } catch (error) {
      if (error instanceof ChecklistBlockerError) {
        const first = error.blockers?.[0]
        if (first?.message) {
          showFlash(first.message)
        } else if (error.message) {
          showFlash(error.message)
        }
        setNodes((currentNodes) =>
          currentNodes.map((candidate) => candidate.id === node.id ? {
            ...candidate,
            workflowStatus: node.workflowStatus,
            isCompleted: node.isCompleted,
            boardStatus: node.boardStatus,
          } : candidate),
        )
        return
      }
      const isVersionConflict = (error as { details?: { code?: string } })?.details?.code === 'VERSION_CONFLICT'
        || ((error as { code?: string })?.code === 'functions/aborted' || (error as { code?: string })?.code === 'aborted')
          && /source lane|record changed/i.test(String((error as { message?: string })?.message))
        || /source lane|record changed/i.test(String((error as { message?: string })?.message))

      if (isVersionConflict) {
        showFlash('This item was updated by someone else. Refreshing…')
        try {
          const { refetchChecklistItemHead } = await import('../../services/checklistReader')
          const fresh = await refetchChecklistItemHead(node.id)
          if (fresh) {
            setNodes((currentNodes) => currentNodes.map((candidate) => candidate.id === node.id ? {
              ...candidate,
              boardStatus: fresh.boardStatus,
              workflowStatus: fresh.workflowStatus,
              isCompleted: fresh.workflowStatus === 'COMPLETED',
              recordVersion: fresh.recordVersion,
            } : candidate))
          }
        } catch {
          // ignore refetch failure
        }
        return
      }
      const errorCode = (error as { code?: string })?.code ?? (error as { details?: { code?: string } })?.details?.code ?? 'UNKNOWN'
      showFlash(`Could not change status: ${errorCode}`)
    }
  }

  const deleteNode = async (node: BoardChecklistItem) => {
    if (!canDeleteChecklist) {
      showFlash('You do not have permission to delete checklist items.')
      return
    }
    deletedSourceIdsRef.current.add(getSourceChecklistId(node.parentContainerId, node))

    const remainingNodes = nodes.filter((candidate) => candidate.id !== node.id)
    await moveChecklistToBin({
      targetId: node.id,
      targetTitle: node.title,
      workspaceId: node.workspaceId,
      board: node.board ?? 'DASHBOARD',
      deletedFromBoard: node.deletedFromBoard,
      parentContainerId: node.parentContainerId,
      currentUser,
      currentDisplayName,
      firestoreDb,
      legacyChecklistUpdate: shouldMirrorNodeToElement(node) ? nodesToElementChecklist(node.parentContainerId, remainingNodes) : undefined,
    })

    if (shouldMirrorNodeToElement(node)) {
      lastChecklistMirrorRef.current = JSON.stringify(nodesToElementChecklist(node.parentContainerId, remainingNodes))
    }
    setNodes(remainingNodes)
    setSelectedNodeId((current) => current === node.id ? null : current)
  }

  const moveNodeToVisionboard = async (node: BoardChecklistItem) => {
    if (!canEditStructure) return
    setVisionboardChecklistItems((current) => {
      if (current.some((candidate) => candidate.id === node.id)) return current
      return [...current, { ...node, board: 'VISIONBOARD' }]
    })
    await deleteNode(node)
    showFlash('Moved to Visionboard.')
  }


  return {
    getNodeOperationalAccess,
    updateTitle,
    updateWeightage,
    emitChecklistCompletion,
    executeNodeCompletion,
    updateNodeVerification,
    updateNodeAssignment,
    getConnectorRendering,
    updateNodeStatus,
    deleteNode,
    moveNodeToVisionboard,
  }
}
